/**
 * Contracts of the economy routes (Phase 5): what the hangar returns, what a purchase answers,
 * and the internal grant that tests and Phase 7's admin panel use. Zod at every boundary
 * (CLAUDE.md conventions); the API serializes through these and the client parses with them.
 */
import { z } from 'zod';
import { COSMETIC_KINDS } from '../data/cosmetic-schema.js';
import { LoadoutSchema } from '../loadout/schemas.js';
const int = z.number().int();
export const CurrencySchema = z.enum(['credits', 'xp']);
/** Sums of the account's ledger rows; never a stored value (CLAUDE.md #6). */
export const BalancesSchema = z
    .object({ credits: int.nonnegative(), xp: int.nonnegative() })
    .strict();
export const HangarMechSchema = z
    .object({
    id: z.string().min(1),
    starter: z.boolean(),
    owned: z.boolean(),
    /** The catalog price; null for starters (always owned) and anything not for sale. */
    price: int.positive().nullable(),
})
    .strict();
/** `GET /api/v1/hangar`: the catalog as the account sees it. */
export const HangarModuleSchema = z
    .object({ id: z.string().min(1), owned: z.boolean(), price: int.positive() })
    .strict();
export const HangarCosmeticSchema = z
    .object({
    id: z.string().min(1),
    kind: z.enum(COSMETIC_KINDS),
    /** Free, bought, or reached by level: the account may equip it now. */
    owned: z.boolean(),
    price: int.nonnegative(),
    levelUnlock: int.positive().nullable(),
})
    .strict();
export const HangarSchema = z
    .object({
    balances: BalancesSchema,
    /** Pilot level from the XP total (level unlocks are implicit, DECISIONS.md). */
    level: int.positive(),
    mechs: z.array(HangarMechSchema),
    modules: z.array(HangarModuleSchema),
    cosmetics: z.array(HangarCosmeticSchema),
    /** Saved loadouts by mech id; mechs without one use the defaults. */
    loadouts: z.record(z.string().min(1), LoadoutSchema),
})
    .strict();
/** `POST /api/v1/hangar/loadouts/:mechId`: the loadout as saved. */
export const SaveLoadoutResultSchema = z
    .object({ ok: z.literal(true), mechId: z.string().min(1), loadout: LoadoutSchema })
    .strict();
export const ItemKindSchema = z.enum(['module', 'cosmetic']);
/** `POST /api/v1/hangar/items/:kind/:id/purchase`. */
export const ItemPurchaseResultSchema = z
    .object({
    ok: z.literal(true),
    kind: ItemKindSchema,
    id: z.string().min(1),
    price: int.positive(),
    /** Credits left after the debit. */
    balance: int.nonnegative(),
})
    .strict();
/** `POST /api/v1/hangar/mechs/:id/purchase`. */
export const PurchaseResultSchema = z
    .object({
    ok: z.literal(true),
    mechId: z.string().min(1),
    price: int.positive(),
    /** Credits left after the debit. */
    balance: int.nonnegative(),
})
    .strict();
/** `POST /api/v1/internal/ledger/grants`: a grant (positive) or a deduction (negative). */
export const GrantRequestSchema = z
    .object({
    /** Caller-chosen; a replay with the same id changes nothing. */
    grantId: z.uuid(),
    userId: z.uuid(),
    currency: CurrencySchema,
    amount: int
        .min(-1_000_000)
        .max(1_000_000)
        .refine((n) => n !== 0, 'amount must not be zero'),
    reason: z.string().min(1).max(200),
})
    .strict();
export const GrantResultSchema = z
    .object({ ok: z.literal(true), inserted: z.boolean(), balances: BalancesSchema })
    .strict();
export const BoardNameSchema = z.enum(['alltime', 'weekly', 'rating', 'mech']);
export const LeaderboardRowSchema = z
    .object({
    rank: int.positive(),
    callsign: z.string(),
    /** The score total on the score boards, the rating on the rating board. */
    value: z.number(),
    matches: int.nonnegative(),
    /** Score boards only. */
    wins: int.nonnegative().nullable(),
    /** Rating board only: the rating deviation. */
    rd: z.number().nullable(),
    /** The caller's own row. */
    mine: z.boolean(),
})
    .strict();
/** `GET /api/v1/leaderboard/:board`. */
export const LeaderboardSchema = z
    .object({
    board: BoardNameSchema,
    /** The mech the per-mech board shows; null on the other boards. */
    mechId: z.string().nullable(),
    /** When the boards were last refreshed (ISO); null before the first refresh. */
    refreshedAt: z.string().nullable(),
    rows: z.array(LeaderboardRowSchema),
    /** The caller's own row when signed in and on the board (it may also be among `rows`). */
    own: LeaderboardRowSchema.nullable(),
    /** Per-mech board: every mech with at least one row, most played first. */
    mechs: z.array(z.object({ mechId: z.string(), players: int.nonnegative() }).strict()),
})
    .strict();
/** `GET /api/v1/profile`: the account's progression as the server holds it. */
export const ProfileSchema = z
    .object({
    callsign: z.string().nullable(),
    balances: BalancesSchema,
    level: z
        .object({
        level: int.positive(),
        xpIntoLevel: int.nonnegative(),
        xpToNext: int.nonnegative(),
        progress: z.number().min(0).max(1),
        capped: z.boolean(),
    })
        .strict(),
    rating: z
        .object({
        rating: z.number(),
        rd: z.number(),
        placed: z.boolean(),
        rankedMatches: int.nonnegative(),
        placementMatches: int.positive(),
        /** Null until placed. */
        tier: z.string().nullable(),
    })
        .strict(),
    record: z
        .object({
        matches: int.nonnegative(),
        wins: int.nonnegative(),
        losses: int.nonnegative(),
        draws: int.nonnegative(),
    })
        .strict(),
})
    .strict();
export const REPORT_REASONS = ['cheating', 'abuse', 'other'];
export const ReportReasonSchema = z.enum(REPORT_REASONS);
/** `POST /api/v1/report`: the opponent of one of the caller's matches. */
export const ReportRequestSchema = z
    .object({
    matchId: z.uuid(),
    reason: ReportReasonSchema,
    details: z.string().max(1000).default(''),
})
    .strict();
export const ReportResultSchema = z
    .object({ ok: z.literal(true), reportId: int.positive() })
    .strict();
//# sourceMappingURL=schemas.js.map